package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import kotlin.math.roundToInt

// COPY RULE: components carry NO user-facing English. Callers pass localized strings (string resources).
// Icons use Material Symbols Outlined via AppIcon (matches Stitch).

/**
 * Saffron "verified" check shown next to verified business names (Material Symbols `verified`).
 * [contentDescription] is the localized accessibility label (string resource); null = decorative.
 */
@Composable
fun VerificationBadge(
    modifier: Modifier = Modifier,
    contentDescription: String? = null,
) {
    AppIcon(
        name = "verified",
        modifier = modifier,
        size = AppTheme.sizes.iconSm,
        tint = AppTheme.colors.secondary,
        filled = true,
        contentDescription = contentDescription,
    )
}

/** Charcoal "Sponsored" label (4px radius). [label] is a localized string (pass from resources). */
@Composable
fun SponsoredBadge(
    label: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = label,
        style = AppTheme.typography.labelSm,
        color = AppTheme.colors.onTertiary,
        modifier = modifier
            .background(AppTheme.colors.tertiary, AppTheme.shapes.sm)
            .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xs),
    )
}

/**
 * Gold accent badge (secondary container) — e.g. the "SPONSORED" tag beside a section title
 * (Stitch 19bc25c5). [label] is a localized string (pass from resources).
 */
@Composable
fun AccentBadge(
    label: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = label,
        style = AppTheme.typography.labelSm,
        color = AppTheme.colors.onSecondaryContainer,
        modifier = modifier
            .background(AppTheme.colors.secondaryContainer, AppTheme.shapes.sm)
            .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xxs),
    )
}

/**
 * Icon + label pill for typed content (e.g. a feed post's Question/Recommendation kind). Callers
 * pass localized [label] and token colors — the per-type color mapping is the caller's domain
 * knowledge, not the component's.
 */
@Composable
fun IconBadge(
    label: String,
    iconName: String,
    containerColor: androidx.compose.ui.graphics.Color,
    contentColor: androidx.compose.ui.graphics.Color,
    modifier: Modifier = Modifier,
) {
    androidx.compose.foundation.layout.Row(
        modifier = modifier
            .background(containerColor, AppTheme.shapes.pill)
            .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xxs),
        verticalAlignment = androidx.compose.ui.Alignment.CenterVertically,
        horizontalArrangement = androidx.compose.foundation.layout.Arrangement.spacedBy(AppTheme.spacing.xxs),
    ) {
        AppIcon(name = iconName, size = AppTheme.sizes.iconSm, tint = contentColor)
        Text(text = label, style = AppTheme.typography.labelSm, color = contentColor)
    }
}

/**
 * Neutral status pill (e.g. "Coming soon", "Closed") — subdued surface, no semantic color so it reads
 * as informational rather than promotional. [label] is a localized string (pass from resources).
 */
@Composable
fun StatusBadge(
    label: String,
    modifier: Modifier = Modifier,
) {
    Text(
        text = label,
        style = AppTheme.typography.labelSm,
        color = AppTheme.colors.onSurfaceVariant,
        modifier = modifier
            .background(AppTheme.colors.surfaceContainerHigh, AppTheme.shapes.pill)
            .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xs),
    )
}

/** Compact unread-count badge. [contentDescription] is localized by the caller. */
@Composable
fun NotificationBadge(
    count: Int,
    contentDescription: String,
    modifier: Modifier = Modifier,
) {
    if (count <= 0) return
    Text(
        text = if (count > 99) "99+" else count.toString(),
        style = AppTheme.typography.labelSm,
        color = AppTheme.colors.onPrimary,
        modifier = modifier
            .sizeIn(
                minWidth = AppTheme.sizes.notificationBadgeMinSize,
                minHeight = AppTheme.sizes.notificationBadgeMinSize,
            )
            .background(AppTheme.colors.primary, AppTheme.shapes.pill)
            .padding(horizontal = AppTheme.spacing.xs, vertical = AppTheme.spacing.xxs)
            .clearAndSetSemantics { this.contentDescription = contentDescription },
    )
}

@Preview
@Composable
private fun NotificationBadgePreview() {
    AppTheme {
        Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
            NotificationBadge(count = 3, contentDescription = 3.toString())
            NotificationBadge(count = 120, contentDescription = 120.toString())
        }
    }
}

/**
 * Saffron rating stars (Material Symbols `star`, FILL=1 for earned stars — matches Stitch) +
 * optional review count.
 *
 * [contentDescription] gives screen readers the VALUE (e.g. a localized "Rated 4.8 out of 5") —
 * without it the glyph row is decorative-only and TalkBack/VoiceOver hears nothing (UX-3). The
 * string comes from the caller (DS components carry no copy).
 */
@Composable
fun RatingStars(
    rating: Float,
    modifier: Modifier = Modifier,
    reviewCount: Int? = null,
    max: Int = 5,
    contentDescription: String? = null,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
        modifier = if (contentDescription != null) {
            modifier.clearAndSetSemantics { this.contentDescription = contentDescription }
        } else {
            modifier
        },
    ) {
        // Round to the nearest half so the star row matches the numeric label (4.75 → 4.5 → ★★★★½),
        // instead of truncating (4.75 → 4 solid stars, disagreeing with the "4.8" text).
        val halves = (rating.coerceIn(0f, max.toFloat()) * 2).roundToInt()
        repeat(max) { index ->
            val fillState = (halves - index * 2).coerceIn(0, 2) // 2 = full, 1 = half, 0 = empty
            AppIcon(
                name = if (fillState == 1) "star_half" else "star",
                size = AppTheme.sizes.iconSm,
                tint = if (fillState > 0) AppTheme.colors.secondary else AppTheme.colors.outlineVariant,
                filled = fillState > 0,
            )
        }
        if (reviewCount != null) {
            Text(
                text = "${ratingLabel(rating)} ($reviewCount)",
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
                modifier = Modifier.padding(start = AppTheme.spacing.xs),
            )
        }
    }
}
